/**
 * Extracts and formats the tax rate from article data
 * @param {Object} article - The article object
 * @returns {number} The tax rate as a percentage (e.g., 19 for 19%)
 */
export const extractTaxRate = (article) => {
	// Try to get tax rate from different possible locations in the article object
	const taxPercentage = article?.tax_percentage || 
		article?.article_price?.tax_percentage ||
		article?.instance?.tax_percentage || 
		article?.instance?.cost?.tax_percentage || 
		null;
	
	// If we found a percentage directly, return it
	if (taxPercentage !== null && taxPercentage !== undefined) {
		return Number(taxPercentage);
	}
	
	// If not, try to calculate from amounts
	const grossAmount = Number(article?.article_price?.total_gross_amount || 0);
	const netAmount = Number(article?.article_price?.total_net_amount || 0);
	
	if (grossAmount > 0 && netAmount > 0) {
		const taxRatio = (grossAmount / netAmount) - 1;
		return Math.round(taxRatio * 100);
	}
	
	// Default to standard German VAT rate if nothing else is available
	return 19;
};

/**
 * Calculates the tax amount for a single item
 * @param {Object} item - The item object
 * @returns {number} The calculated tax amount
 */
export const calculateTaxAmount = (item) => {
	const price = Number(item.price || 0);
	const quantity = parseInt(item.quantity || 1, 10);
	const taxRate = item.taxRate || extractTaxRate(item.originalArticle) || 19;
	
	// Calculate using net-to-gross formula
	const grossAmount = price * quantity;
	const netAmount = grossAmount / (1 + taxRate / 100);
	return grossAmount - netAmount;
};

/**
 * Groups tax amounts by tax rate, properly accounting for quantities
 * @param {Array} items - Array of order items
 * @returns {Object} Object with tax rates as keys and amounts as values
 */
export const groupTaxesByRate = (items = []) => {
	const taxGroups = {};
	
	items.forEach(item => {
		// Get tax rate for this item
		const taxRate = item.taxRate || extractTaxRate(item.originalArticle) || 19;
		
		// Calculate the tax amount for this item
		let taxAmount = 0;
		const quantity = parseInt(item.quantity || 1, 10);
		const price = Number(item.price || 0);
		
		// Calculate tax amount: grossAmount - netAmount
		const grossAmount = price * quantity;
		const netAmount = grossAmount / (1 + taxRate / 100);
		taxAmount = grossAmount - netAmount;
		
		// Initialize tax rate group if it doesn't exist
		if (!taxGroups[taxRate]) {
			taxGroups[taxRate] = 0;
		}
		
		// Add tax amount to corresponding group
		taxGroups[taxRate] += taxAmount;
	});
	
	return taxGroups;
};
